import React from 'react';
import { IconMap, AVAILABLE_ICONS, type IconName } from './icons';
import { isEmoji } from '../../../utils/iconUtils';
import './Icon.scss';

export interface IconProps {
  /** Nombre del icono a renderizar */
  name: string;
  /** Tamaño del icono en píxeles */
  size?: number;
  /** Color del icono. Puede ser cualquier valor CSS válido, incluyendo variables CSS como 'var(--primary-color)' */
  color?: string;
  /** Clases CSS adicionales */
  className?: string;
  /** Texto alternativo para accesibilidad */
  alt?: string;
  /** Variante de color predefinida basada en las variables globales */
  variant?: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' | 'white' | 'black';
}

const Icon: React.FC<IconProps> = ({ 
  name, 
  size = 20, 
  color,
  className = '', 
  alt,
  variant = 'default'
}) => {
  // Si es un emoji, renderizarlo directamente
  if (isEmoji(name)) {
    return (
      <span 
        className={`icon icon--emoji ${className}`}
        style={{ fontSize: `${size}px` }}
        role="img"
        aria-label={alt || name}
      >
        {name}
      </span>
    );
  }

  // Buscar el componente de icono en el mapa
  const IconComponent = IconMap[name as IconName];

  if (IconComponent) {
    // Determinar el color final basado en variant y color prop
    let finalColor = color;
    
    // Si no se proporciona un color explícito pero hay un variant, usar la clase CSS
    const variantClass = variant !== 'default' ? `icon--${variant}` : '';
    const classes = `icon icon--svg ${variantClass} ${className}`.trim();

    return (
      <IconComponent 
        size={size}
        color={finalColor}
        className={classes}
      />
    );
  }

  // Si no se encuentra el icono, mostrar un fallback
  if (process.env.NODE_ENV === 'development') {
    console.warn(
      `Icon "${name}" not found. Available icons: ${AVAILABLE_ICONS.join(', ')}`
    );
  }
  
  return (
    <span 
      className={`icon icon--fallback ${className}`}
      style={{ fontSize: `${size}px` }}
      title={`Icon "${name}" not found`}
      role="img"
      aria-label={alt || `Icon ${name} not found`}
    >
      ?
    </span>
  );
};

export default Icon;
